import type { MouseEvent } from "react";

import { IIcon } from "../Icon/Icon";
import { IImageProps } from "../Image/Image";

export enum ButtonComponent {
  DEFAULT,
  LINK,
}

export type Design = "default" | "line" | "underline" | "shimmer";
export type Color = "default" | "secondary" | "transparent";
export type Type = "button" | "submit";

export const sizeClassMap = {
  large: "w-full lg:w-[200px] max-w-full",
  default: "w-full max-w-full lg:max-w-[358px]",
  "full-width": "w-full",
  "fit-content": "w-fit",
};

export type Size = keyof typeof sizeClassMap;

export interface IButton {
  id: string;
  name: string;
  color?: Color;
  design?: Design;
}

export interface ILink {
  id: string;
  name: string;
  to: string;
  color?: Color;
  design?: Design;
  testId?: string;
  keepQueryParams?: boolean;
  withIcon?: boolean;
  size?: Size;
}

export interface IHeaderLinkChild extends IHeaderLink {
  image?: IImageProps;
  tag?: string;
  seeMoreText?: string;
}

export type HeaderLinkType = "default" | "cta" | "authMenu" | "logout" | "icon";

export interface IHeaderLink extends ILink {
  image?: IImageProps;
  children?: IHeaderLinkChild[];
  type?: HeaderLinkType;
  withAssociatedDiscount?: boolean;
  icon?: IIcon;
  /** Runs before the optional parent `onClick` (e.g. menu close). Call `preventDefault()` to block navigation. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Translation key for this link's display name, scoped to
   * `Components.Header.Navigation`. When set, the rendered component resolves
   * this via `useTranslations` and ignores `name`.
   */
  nameKey?: string;
}

type DesignClassStyle = {
  [key in Color]: string;
};

export interface IDesignClassStyleWithBase extends DesignClassStyle {
  base: string;
}

type DesignClassMap = {
  [key in Design]: IDesignClassStyleWithBase
};

export const designClassMap: DesignClassMap = {
  default: {
    base: "data-[disabled=true]:!bg-grey-400 data-[disabled=true]:pointer-events-none disabled:bg-grey-400 disabled:text-white disabled:cursor-not-allowed block min-w-fit h-10 lg:min-h-[50px] flex justify-center items-center px-3 lg:px-6 py-2 uppercase rounded-full text-center font-display opacity-100 lg:hover:opacity-70 transition-opacity duration-300",
    default: "bg-black text-white lg:theme-header:bg-light-grey lg:theme-header:text-black theme-brand:bg-white theme-brand:text-black theme-dark:bg-light-grey theme-dark:text-black theme-footer:bg-white theme-footer:text-black theme-brand:theme-light:bg-black theme-brand:theme-light:text-white theme-light-blue:bg-black theme-light-blue:text-white",
    secondary: "text-white theme-brand-alt:bg-white theme-brand-alt:text-black theme-brand-alt:theme-brand:bg-black theme-brand-alt:theme-light-grey:text-white theme-brand-alt:theme-light-grey:bg-black theme-brand-alt:theme-brand:text-white theme-brand:bg-black theme-brand:text-white theme-brand:disabled:bg-grey-400 theme-light:bg-brand theme-light:text-black theme-dark-alt:text-black theme-dark-alt:bg-brand theme-light:bg-brand theme-light:text-black",
    transparent: "text-black",
  },
  line: {
    base: "disabled:opacity-30 disabled:cursor-not-allowed block min-w-fit h-10 lg:min-h-[50px] flex justify-center items-center border-2 block px-4 lg:px-6 py-2 uppercase rounded-full text-center font-display opacity-100 lg:hover:opacity-70 transition-opacity duration-300",
    default: "text-black border-black theme-light-grey:bg-white",
    secondary: "text-white border-white",
    transparent: "text-black",
  },
  underline: {
    base: "disabled:opacity-30 disabled:cursor-not-allowed block min-w-fit h-10 lg:min-h-[50px] flex justify-center items-center block px-4 lg:px-6 py-2 underline text-center font-display opacity-100 lg:hover:opacity-70 transition-opacity duration-300",
    default: "text-black",
    secondary: "text-white border-white",
    transparent: "text-black",
  },
  shimmer: {
    base: "data-[disabled=true]:!bg-grey-400 data-[disabled=true]:pointer-events-none disabled:bg-grey-400 disabled:text-white disabled:cursor-not-allowed block min-w-fit min-h-9 text-center flex justify-center items-center px-3 lg:px-6 py-2 rounded-full text-center font-display opacity-100 lg:hover:opacity-70 transition-opacity duration-300 animate-shimmer relative before:z-[-1] before:top-0 before:left-0 before:absolute before:block before:w-full before:block before:h-full bg-[linear-gradient(110deg,rgba(213,231,9,1),33%,rgba(255,255,255,1),55%,rgba(213,231,9,1))] bg-[length:200%_100%] before:rounded-full",
    default: "before:bg-brand text-black lg:theme-header:before:bg-light-grey lg:theme-header:text-black theme-brand:before:bg-white theme-brand:text-black theme-dark:before:bg-light-grey theme-dark:text-black theme-footer:before:bg-white theme-footer:text-black theme-brand:theme-light:before:bg-black theme-brand:theme-light:text-white",
    secondary: "text-white theme-brand-alt:before:bg-white theme-brand-alt:text-black theme-brand-alt:theme-brand:before:bg-black theme-brand-alt:theme-light-grey:text-white theme-brand-alt:theme-light-grey:before:bg-black theme-brand-alt:theme-brand:text-white theme-brand:before:bg-black theme-brand:text-white theme-brand:disabled:before:bg-grey-400 theme-light:before:bg-brand theme-light:text-black",
    transparent: "text-black",
  },
};
